// src/pages/itsm/ITSMAgents.tsx
import { useState, useEffect } from 'react'
// @ts-ignore
import { supabase } from '../../supabaseClient'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Sun, Moon, Folder, Phone } from 'lucide-react'
import { useTheme } from '../../hooks/useTheme'
import { T, Card, Btn, Pill, EmptyState, Avatar, ConfirmDialog, showToast, ToastHost } from '../../components/ui/UI'



export default function ITSMAgents() {
    const navigate = useNavigate()
    const { theme, toggleTheme } = useTheme()
    const [agents, setAgents] = useState<any[]>([])
    const [showAdd, setShowAdd] = useState(false)
    const [loading, setLoading] = useState(true)
    const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'agent', department: '', max_tickets: 20 })
    const [confirmDeactivate, setConfirmDeactivate] = useState<{ id: number; is_active: boolean; name: string } | null>(null)

    useEffect(() => { fetchAgents() }, [])

    const fetchAgents = async () => {
        const { data } = await supabase.from('itsm_agents').select('*').order('name')
        setAgents(data || [])
        setLoading(false)
    }

    const saveAgent = async () => {
        await supabase.from('itsm_agents').insert([form])
        setForm({ name: '', email: '', phone: '', role: 'agent', department: '', max_tickets: 20 })
        setShowAdd(false)
        fetchAgents()
        showToast('Agent added', 'success')
    }

    const toggleAgent = async (id: number, is_active: boolean) => {
        await supabase.from('itsm_agents').update({ is_active: !is_active }).eq('id', id)
        fetchAgents()
        showToast(is_active ? 'Agent deactivated' : 'Agent activated', 'success')
    }

    const ROLE_CONFIG: Record<string, { label: string; tone: 'info' | 'violet' | 'warning' | 'success' | 'danger' }> = {
        agent: { label: 'Agent', tone: 'info' },
        senior_agent: { label: 'Senior Agent', tone: 'violet' },
        team_lead: { label: 'Team Lead', tone: 'warning' },
        manager: { label: 'Manager', tone: 'success' },
        admin: { label: 'Admin', tone: 'danger' },
    }

    return (
        <div className="jr-app" data-theme={theme} style={s.page}>
            <button onClick={() => navigate('/dashboard/itsm')} style={s.back}>← Back to ITSM</button>
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Agents</h1>
                    <p style={s.subtitle}>{agents.filter(a => a.is_active).length} active agents</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
                    <Btn variant="primary" style={undefined} onClick={() => setShowAdd(true)}>+ Add Agent</Btn>
                </div>
            </div>

            <div style={s.grid}>
                {agents.map(agent => {
                    const role = ROLE_CONFIG[agent.role] || ROLE_CONFIG.agent
                    return (
                        <Card key={agent.id} style={{ opacity: agent.is_active ? 1 : 0.5 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Avatar name={agent.name} size={40} />
                                    <div>
                                        <p style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{agent.name}</p>
                                        <p style={{ fontSize: 12, color: T.text2 }}>{agent.email}</p>
                                    </div>
                                </div>
                                <Pill tone={role.tone}>{role.label.toUpperCase()}</Pill>
                            </div>
                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `0.5px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    {agent.department && <p style={{ fontSize: 12, color: T.text2, display: 'flex', alignItems: 'center', gap: 5 }}><Folder size={11} />{agent.department}</p>}
                                    {agent.phone && <p style={{ fontSize: 12, color: T.text2, display: 'flex', alignItems: 'center', gap: 5 }}><Phone size={11} />{agent.phone}</p>}
                                    <p style={{ fontSize: 11, color: T.text3 }}>Max {agent.max_tickets} tickets</p>
                                </div>
                                <Btn size="sm" style={agent.is_active ? { background: T.dangerBg, color: T.danger, borderColor: T.dangerBorder } : { background: T.successBg, color: T.success, borderColor: T.successBorder }}
                                    onClick={() => setConfirmDeactivate({ id: agent.id, is_active: agent.is_active, name: agent.name })}>
                                    {agent.is_active ? 'Deactivate' : 'Activate'}
                                </Btn>
                            </div>
                        </Card>
                    )
                })}
                {!loading && agents.length === 0 && (
                    <div style={{ gridColumn: 'span 3' }}>
                        <EmptyState title="No agents yet" sub='Click "+ Add Agent" to create one' />
                    </div>
                )}
            </div>

            {showAdd && (
                <div style={s.overlay}>
                    <div style={s.modal}>
                        <h2 style={s.modalTitle}>New Agent</h2>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div style={s.field}>
                                <label style={s.label}>Full Name *</label>
                                <input style={s.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Email *</label>
                                <input style={s.input} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Phone</label>
                                <input style={s.input} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Role</label>
                                <select style={s.input} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                                    <option value="agent">Agent</option>
                                    <option value="senior_agent">Senior Agent</option>
                                    <option value="team_lead">Team Lead</option>
                                    <option value="manager">Manager</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Department</label>
                                <input style={s.input} value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>Max Tickets</label>
                                <input style={s.input} type="number" value={form.max_tickets} onChange={e => setForm({ ...form, max_tickets: Number(e.target.value) })} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                            <Btn style={s.btnOutline} onClick={() => setShowAdd(false)}>Cancel</Btn>
                            <Btn variant="primary" style={undefined} onClick={saveAgent}>Save Agent</Btn>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={!!confirmDeactivate}
                onClose={() => setConfirmDeactivate(null)}
                onConfirm={() => confirmDeactivate && toggleAgent(confirmDeactivate.id, confirmDeactivate.is_active)}
                title={confirmDeactivate?.is_active ? 'Deactivate agent?' : 'Activate agent?'}
                message={confirmDeactivate?.is_active
                    ? `${confirmDeactivate?.name} will no longer be assignable to new tickets.`
                    : `${confirmDeactivate?.name} will become assignable to new tickets again.`}
                confirmLabel={confirmDeactivate?.is_active ? 'Deactivate' : 'Activate'}
                danger={!!confirmDeactivate?.is_active}
            />
            <ToastHost />
        </div>
    )
}

function ThemeToggle({ theme, toggleTheme }: { theme: 'light' | 'dark'; toggleTheme: () => void }) {
    return (
        <button onClick={toggleTheme} aria-label="Toggle theme" style={{
            width: 34, height: 34, borderRadius: T.radiusSm, background: T.surface,
            border: `1px solid ${T.borderStrong}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: T.text2,
        }}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </button>
    )
}

const s: Record<string, React.CSSProperties> = {
    page: { padding: '32px 24px', maxWidth: 1200, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
    title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 4 },
    back: { background: 'none', border: 'none', color: T.accent, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0, fontWeight: 500 },
    subtitle: { fontSize: 13, color: T.text2 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 },
    btnOutline: { flex: 1, background: 'transparent' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { background: T.surface, borderRadius: 12, padding: 28, width: '100%', maxWidth: 520, border: `1px solid ${T.border}` },
    modalTitle: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20 },
    field: { marginBottom: 14 },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 5 },
    input: { width: '100%', height: 36, border: `0.5px solid ${T.borderStrong}`, borderRadius: 8, padding: '0 10px', fontSize: 13, color: T.text, outline: 'none', background: T.surface },
}
